import type { DashboardOverview } from '../components/dashboard/DashboardStatsGrid';

const MODULE_JOB_STATS = [
  { module: 'transportation', totalKey: 'totalRides', completedKey: 'completedRides', cancelledKey: 'cancelledRides', activeKey: 'activeRides', pendingKey: 'pendingRides' },
  { module: 'porter', totalKey: 'totalPorterOrders', completedKey: 'completedPorterOrders', cancelledKey: 'cancelledPorterOrders', activeKey: 'activePorterOrders', pendingKey: 'pendingPorterOrders' },
  { module: 'on_demand', totalKey: 'totalOnDemandBookings', completedKey: 'completedOnDemandBookings', cancelledKey: 'cancelledOnDemandBookings', activeKey: 'activeOnDemandBookings', pendingKey: 'pendingOnDemandBookings' },
] as const;

const ROLE_MODULE_FOCUS: Record<string, readonly string[]> = {
  driver: ['transportation', 'porter'],
  delivery_partner: ['porter'],
  service_provider: ['on_demand'],
};

const MODULE_LABELS: Record<string, string> = {
  transportation: 'Transport',
  porter: 'Porter',
  on_demand: 'Services',
};

const TODAY_MODULE_STATS = [
  { module: 'transportation', totalKey: 'todayRides', chipLabel: 'rides' },
  { module: 'porter', totalKey: 'todayPorterOrders', chipLabel: 'porter' },
  { module: 'on_demand', totalKey: 'todayOnDemandBookings', chipLabel: 'home' },
] as const;

type JobStatKey = keyof DashboardOverview;

function resolveModules(
  enabledModules: string[],
  isSuperAdmin?: boolean,
  role?: string,
): string[] {
  const roleFocus = role ? ROLE_MODULE_FOCUS[role] : undefined;
  const tenantModules = enabledModules.length
    ? enabledModules.filter((m) => m !== 'food' && m !== 'grocery')
    : MODULE_JOB_STATS.map((m) => m.module);

  if (isSuperAdmin) {
    return MODULE_JOB_STATS.map((m) => m.module);
  }

  if (roleFocus?.length) {
    return tenantModules.filter((module) => roleFocus.includes(module));
  }

  return tenantModules;
}

function roundRate(numerator: number, denominator: number): number {
  return denominator > 0 ? Math.round((numerator / denominator) * 10000) / 100 : 0;
}

export function formatPercent(value: number): string {
  return `${Number(value || 0).toFixed(2)}%`;
}

export interface ServiceStatusInput {
  count?: number;
  completed: number;
  cancelled: number;
  active: number;
  pending: number;
  other?: number;
}

export interface ServiceStatusSegment {
  key: string;
  label: string;
  count: number;
  rate: number;
  color: string;
}

const SERVICE_STATUS_COLORS = {
  completed: '#16a34a',
  cancelled: '#dc2626',
  active: '#0891b2',
  pending: '#ca8a04',
  other: '#64748b',
} as const;

/** Sum of displayed status buckets — should match the service total shown in analytics. */
export function getServiceAccountedTotal({
  count,
  completed,
  cancelled,
  active,
  pending,
  other,
}: ServiceStatusInput): number {
  const accounted = completed + cancelled + active + pending + (other ?? 0);
  return accounted > 0 ? accounted : (count ?? 0);
}

/** Build status segments whose rates sum to 100% of the service total. */
export function buildServiceStatusSegments(
  input: ServiceStatusInput,
  labels?: Partial<Record<'completed' | 'active', string>>,
): ServiceStatusSegment[] {
  const total = getServiceAccountedTotal(input);
  const segments: Array<Omit<ServiceStatusSegment, 'rate'>> = [
    { key: 'completed', label: labels?.completed ?? 'Completed', count: input.completed, color: SERVICE_STATUS_COLORS.completed },
    { key: 'cancelled', label: 'Cancelled', count: input.cancelled, color: SERVICE_STATUS_COLORS.cancelled },
    { key: 'active', label: labels?.active ?? 'In progress', count: input.active, color: SERVICE_STATUS_COLORS.active },
    { key: 'pending', label: 'Pending', count: input.pending, color: SERVICE_STATUS_COLORS.pending },
  ];

  const otherCount = input.other ?? Math.max(0, total - segments.reduce((sum, seg) => sum + seg.count, 0));
  if (otherCount > 0) {
    segments.push({ key: 'other', label: 'Other', count: otherCount, color: SERVICE_STATUS_COLORS.other });
  }

  const withRates = segments.map((seg) => ({
    ...seg,
    rate: total > 0 ? roundRate(seg.count, total) : 0,
  }));

  return normalizeStatusRates(withRates, total);
}

function normalizeStatusRates(segments: ServiceStatusSegment[], total: number): ServiceStatusSegment[] {
  if (!total || !segments.length) return segments;

  const sum = segments.reduce((acc, seg) => acc + seg.rate, 0);
  if (Math.abs(sum - 100) < 0.015 || sum === 0) return segments;

  const delta = Math.round((100 - sum) * 100) / 100;
  const targetIdx = segments.reduce(
    (bestIdx, seg, idx, arr) => (seg.count > arr[bestIdx].count ? idx : bestIdx),
    0,
  );

  return segments.map((seg, idx) => (
    idx === targetIdx ? { ...seg, rate: Math.max(0, Math.round((seg.rate + delta) * 100) / 100) } : seg
  ));
}

export function formatServiceStatusMixSummary(segments: ServiceStatusSegment[]): string {
  const visible = segments.filter((seg) => seg.count > 0);
  if (!visible.length) return 'No jobs yet';
  return visible.map((seg) => `${seg.count.toLocaleString()} ${seg.label.toLowerCase()} (${formatPercent(seg.rate)})`).join(' · ');
}

export function aggregateJobStats(
  overview: DashboardOverview = {},
  enabledModules: string[] = [],
  options?: { isSuperAdmin?: boolean; role?: string },
) {
  const allowedModules = resolveModules(enabledModules, options?.isSuperAdmin, options?.role);
  const relevant = MODULE_JOB_STATS.filter((m) => allowedModules.includes(m.module));

  const totalJobs = relevant.reduce(
    (sum, m) => sum + (Number(overview[m.totalKey as JobStatKey]) || 0),
    0,
  );
  const completedJobs = relevant.reduce(
    (sum, m) => sum + (Number(overview[m.completedKey as JobStatKey]) || 0),
    0,
  );
  const cancelledJobs = relevant.reduce(
    (sum, m) => sum + (Number(overview[m.cancelledKey as JobStatKey]) || 0),
    0,
  );
  const activeJobs = relevant.reduce(
    (sum, m) => sum + (Number(overview[m.activeKey as JobStatKey]) || 0),
    0,
  );
  const pendingJobs = relevant.reduce(
    (sum, m) => sum + (Number(overview[m.pendingKey as JobStatKey]) || 0),
    0,
  );

  const pendingByModule = relevant
    .map((m) => ({
      module: m.module,
      label: MODULE_LABELS[m.module] || m.module,
      count: Number(overview[m.pendingKey as JobStatKey]) || 0,
    }))
    .filter((row) => row.count > 0);

  const activeByModule = relevant
    .map((m) => ({
      module: m.module,
      label: MODULE_LABELS[m.module] || m.module,
      count: Number(overview[m.activeKey as JobStatKey]) || 0,
    }))
    .filter((row) => row.count > 0);

  return {
    totalJobs,
    completedJobs,
    cancelledJobs,
    activeJobs,
    pendingJobs,
    pendingByModule,
    activeByModule,
    completionRate: roundRate(completedJobs, totalJobs),
    cancelRate: roundRate(cancelledJobs, totalJobs),
  };
}

/** Sum per-service active counts — should match overview.activeJobs when all modules are enabled. */
export function sumActiveByService(overview: DashboardOverview = {}): number {
  return MODULE_JOB_STATS.reduce(
    (sum, m) => sum + (Number(overview[m.activeKey as JobStatKey]) || 0),
    0,
  );
}

export function formatNeedsAssignmentLabel(count: number): string {
  return count === 1
    ? '1 job needs assignment'
    : `${count.toLocaleString()} jobs need assignment`;
}

export function formatInProgressAlertLabel(count: number): string {
  return count === 1
    ? '1 job in progress'
    : `${count.toLocaleString()} jobs in progress`;
}

export function formatPendingHint(
  pendingJobs: number,
  pendingByModule: Array<{ label: string; count: number }>,
): string {
  if (pendingJobs <= 0) return 'No jobs awaiting action';
  if (pendingByModule.length === 0) return `${pendingJobs.toLocaleString()} awaiting action`;
  return pendingByModule.map((row) => `${row.count.toLocaleString()} ${row.label}`).join(' · ');
}

export function formatActiveHint(
  activeJobs: number,
  activeByModule: Array<{ label: string; count: number }>,
): string {
  if (activeJobs <= 0) return 'No jobs in progress';
  if (activeByModule.length === 0) return `${activeJobs.toLocaleString()} in progress`;
  const breakdown = activeByModule.map((row) => `${row.count.toLocaleString()} ${row.label}`).join(' · ');
  return `${breakdown} (${activeJobs.toLocaleString()} total)`;
}

/** Status breakdown for dashboard "By service" cards — includes cancelled so totals reconcile. */
export function formatServiceBreakdownHint({
  total,
  completed,
  pending,
  active,
  cancelled,
  other: otherCount,
  completedLabel = 'completed',
  activeLabel = 'active',
}: {
  total?: number;
  completed: number;
  pending: number;
  active: number;
  cancelled: number;
  other?: number;
  completedLabel?: string;
  activeLabel?: string;
}): string {
  const segments: string[] = [];
  const push = (count: number, label: string) => {
    if (count > 0) segments.push(`${count.toLocaleString()} ${label}`);
  };

  push(completed, completedLabel);
  push(pending, 'pending');
  push(active, activeLabel);
  push(cancelled, 'cancelled');

  const accounted = completed + pending + active + cancelled;
  const other = otherCount ?? (total != null ? Math.max(0, total - accounted) : 0);
  push(other, 'other');

  if (total != null) {
    return segments.length
      ? `${segments.join(' · ')} (${total.toLocaleString()} total)`
      : `${total.toLocaleString()} total`;
  }

  return segments.join(' · ') || 'No rides yet';
}

export function aggregateTodayActivity(
  overview: DashboardOverview = {},
  enabledModules: string[] = [],
  options?: { isSuperAdmin?: boolean; role?: string },
) {
  const allowedModules = resolveModules(enabledModules, options?.isSuperAdmin, options?.role);
  const relevant = TODAY_MODULE_STATS.filter((m) => allowedModules.includes(m.module));

  const total = relevant.reduce(
    (sum, m) => sum + (Number(overview[m.totalKey as JobStatKey]) || 0),
    0,
  );

  const byModule = relevant
    .map((m) => ({
      module: m.module,
      label: m.chipLabel,
      count: Number(overview[m.totalKey as JobStatKey]) || 0,
    }))
    .filter((row) => row.count > 0);

  return { total, byModule };
}
